// Anke, 2026-07-16 — "aneinander ausrichten": with several objects selected, the
// six align buttons snap them to a shared edge.
//
// Reference = the OUTER edge of the whole selection (Anke's decision): "left"
// moves every object to the left edge of the leftmost one, which itself stays
// put. That edge is already visible on the board, so the outcome is predictable
// without an invisible "anchor object" the user would have to keep track of.
//
// PURE geometry — no Konva, no BoardState. It only decides WHERE things go and
// hands back `Move`s; `MoveObjectsCommand` does the writing and owns the tricky
// parts (line endpoints, anchor detach rules, one undo step for the lot).

import type { Move } from './move-objects.command.js';
import type { BoardObject } from '../interfaces/board-object.interface.js';
import { rotatePointAround } from '../../objects/line-anchor.js';

/** The six edges an alignment can snap to. Matches the `action-align-*` ids. */
export type AlignMode = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';

const HORIZONTAL = new Set<AlignMode>(['left', 'hcenter', 'right']);

/**
 * Below this an object counts as already aligned and is not moved.
 *
 * Needed because a rotated object's edges run through sin/cos, so an edge that
 * is mathematically flush arrives as ~1e-15 off. Compared exactly, that would
 * move it an invisible distance AND push an undo step for a click that changed
 * nothing. A millionth of a board unit is far below anything renderable, so the
 * tolerance can never swallow a move the user meant.
 */
const ALIGNED_EPSILON = 1e-6;

export interface AlignRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The axis-aligned box an object visibly occupies.
 *
 * Every object type carries its own `x/y/width/height` — lines included, which
 * keep theirs in sync with their endpoints (`endpointBounds`) — so there is no
 * per-type case to write here.
 *
 * Rotation is the one thing the stored box does NOT express: it describes the
 * UNROTATED rectangle, and Konva turns the node around its origin (`obj.x`/
 * `obj.y`) — the same convention `anchorBoundsOf` documents. A turned object can
 * therefore stick out well past its stored `x`, so aligning by the stored box
 * would leave that overhang hanging over the edge. Rotating the four corners and
 * taking their span puts the alignment on the edge the user actually sees.
 */
export function alignRectOf(obj: Readonly<BoardObject>): AlignRect {
  const { x, y, width, height } = obj;
  const rotation = obj.rotation ?? 0;
  if (!rotation) return { x, y, width, height };

  const corners = [
    { px: x, py: y },
    { px: x + width, py: y },
    { px: x + width, py: y + height },
    { px: x, py: y + height },
  ].map((c) => rotatePointAround(c.px, c.py, x, y, rotation));

  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}

/** Where `rect` currently sits on the aligned axis, for the given mode. */
function currentEdge(rect: AlignRect, mode: AlignMode): number {
  switch (mode) {
    case 'left':    return rect.x;
    case 'hcenter': return rect.x + rect.width / 2;
    case 'right':   return rect.x + rect.width;
    case 'top':     return rect.y;
    case 'vcenter': return rect.y + rect.height / 2;
    case 'bottom':  return rect.y + rect.height;
  }
}

/** The edge of the whole selection that everything snaps to. */
function targetEdge(rects: readonly AlignRect[], mode: AlignMode): number {
  const horizontal = HORIZONTAL.has(mode);
  const lows = rects.map((r) => (horizontal ? r.x : r.y));
  const highs = rects.map((r) => (horizontal ? r.x + r.width : r.y + r.height));
  const min = Math.min(...lows);
  const max = Math.max(...highs);
  switch (mode) {
    case 'left':
    case 'top':     return min;
    case 'right':
    case 'bottom':  return max;
    // The centre of the selection as a whole — NOT the average of the objects'
    // centres, which would drift toward wherever the most objects happen to sit.
    case 'hcenter':
    case 'vcenter': return (min + max) / 2;
  }
}

/**
 * Work out where each object has to go. Returns one `Move` per object that
 * actually has to travel — an already-aligned object is left out, so a click
 * that changes nothing pushes no (empty) undo step.
 *
 * Fewer than two objects yields nothing: aligning needs a partner, and there is
 * no page or canvas edge to fall back on (the board is endless).
 */
export function computeAlignMoves(
  objs: readonly Readonly<BoardObject>[],
  mode: AlignMode,
): Move[] {
  if (objs.length < 2) return [];

  const rects = objs.map(alignRectOf);
  const target = targetEdge(rects, mode);
  const horizontal = HORIZONTAL.has(mode);

  const moves: Move[] = [];
  objs.forEach((obj, i) => {
    const delta = target - currentEdge(rects[i]!, mode);
    if (Math.abs(delta) < ALIGNED_EPSILON) return;
    // Shift the object's POSITION by the delta its visible edge needs. The two
    // differ for a rotated object (its box starts elsewhere than its x/y), which
    // is exactly why the delta — not the target — is what gets applied.
    moves.push({
      id: obj.id,
      from: { x: obj.x, y: obj.y },
      to: horizontal ? { x: obj.x + delta, y: obj.y } : { x: obj.x, y: obj.y + delta },
    });
  });
  return moves;
}
